import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Globe } from 'lucide-react';

// Bandeiras como SVG inline para evitar dependências externas
const flags = {
  pt: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="w-5 h-5">
      <rect width="512" height="512" fill="#009c3b"/>
      <path d="M256 100 L462 256 L256 412 L50 256 Z" fill="#ffdf00"/>
      <circle cx="256" cy="256" r="80" fill="#002776"/>
      <path d="M170 256 C170 220, 200 190, 256 190 C312 190, 342 220, 342 256" fill="none" stroke="#fff" strokeWidth="8"/>
    </svg>
  ),
  en: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="w-5 h-5">
      {/* USA Flag */}
      <rect width="512" height="512" fill="#B22234"/>
      <rect y="39" width="512" height="30" fill="#fff"/>
      <rect y="98" width="512" height="30" fill="#fff"/>
      <rect y="157" width="512" height="30" fill="#fff"/>
      <rect y="216" width="512" height="30" fill="#fff"/>
      <rect y="275" width="512" height="30" fill="#fff"/>
      <rect y="334" width="512" height="30" fill="#fff"/>
      <rect y="393" width="512" height="30" fill="#fff"/>
      <rect y="452" width="512" height="30" fill="#fff"/>
      <rect width="205" height="275" fill="#3C3B6E"/>
    </svg>
  ),
  es: (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" className="w-5 h-5">
      <rect width="512" height="128" fill="#AA151B"/>
      <rect y="128" width="512" height="256" fill="#F1BF00"/>
      <rect y="384" width="512" height="128" fill="#AA151B"/>
    </svg>
  ),
};

const languageNames = {
  pt: 'Português',
  en: 'English',
  es: 'Español',
};

export const LanguageSelector = ({ variant = 'default', showLabel = false }) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language?.split('-')[0] || 'pt';

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18nextLng', lang);
  };

  if (variant === 'flags-only') {
    // Versão compacta apenas com bandeiras lado a lado
    return (
      <div className="flex items-center gap-1">
        {Object.keys(flags).map((lang) => (
          <button
            key={lang}
            onClick={() => handleLanguageChange(lang)}
            className={`p-1.5 rounded-md transition-all hover:bg-white/10 ${
              currentLang === lang 
                ? 'ring-2 ring-primary bg-white/10' 
                : 'opacity-60 hover:opacity-100'
            }`}
            title={languageNames[lang]}
            data-testid={`lang-flag-${lang}`}
          >
            {flags[lang]}
          </button>
        ))}
      </div>
    );
  }

  // Versão dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-2 text-muted-foreground hover:text-foreground"
          data-testid="language-selector-btn"
        >
          {flags[currentLang] || <Globe className="w-4 h-4" />}
          {showLabel && <span className="hidden sm:inline">{languageNames[currentLang]}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[140px]">
        {Object.entries(languageNames).map(([code, name]) => (
          <DropdownMenuItem
            key={code}
            onClick={() => handleLanguageChange(code)}
            className={`flex items-center gap-2 cursor-pointer ${
              currentLang === code ? 'bg-primary/10 text-primary' : ''
            }`}
            data-testid={`lang-option-${code}`}
          >
            {flags[code]}
            <span>{name}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
